import React, { useEffect, useState } from 'react';
import { ShieldCheck, Settings2 } from 'lucide-react';
import FormModal from '../../components/Modals/FormModal';
import { useRole } from '../../hooks/useRole';
import { useUser } from '../../hooks/useUser';
import { evaluatePassword } from '../../utils/passwordStrength';
import BranchService from '../../services/branch/BranchService';

import UserBasicInfo from './UserForm/UserBasicInfo';
import UserPasswordSection from './UserForm/UserPasswordSection';
import UserRoleSelector from './UserForm/UserRoleSelector';
import UserBranchSelector from './UserForm/UserBranchSelector';
import UserStatusToggles from './UserForm/UserStatusToggles';
import UserSubmitButton from './UserForm/UserSubmitButton';

interface UserEditPopupProps {
  popupOpen: boolean;
  onClose: () => void;
  onUpdate: (userData: any) => void;
}

const UserEditPopup: React.FC<UserEditPopupProps> = ({
  popupOpen,
  onClose,
  onUpdate,
}) => {

  const { fetchRoles, dropdownOptions } = useRole();
  const [branchOptions, setBranchOptions] = useState<{ id: number; name: string }[]>([]);

  const {
    form,
    updateField,
    isSubmitting,
    errors,
    setErrors,
  } = useUser();


  // ✅ Solo carga roles y sucursales al abrir
  useEffect(() => {
    if (popupOpen) {
      fetchRoles();
      BranchService.fetchDropdown().then(setBranchOptions);
    }
  }, [popupOpen]);


  // ✅ Validación exclusiva para update
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Username
    if (!form.username || form.username.trim().length < 3) {
      newErrors.username = 'El nombre debe tener al menos 3 caracteres.';
    }

    // Email
    if (
      !form.email ||
      !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email.trim())
    ) {
      newErrors.email = 'Correo electrónico no válido.';
    }

    // Roles
    if (!form.roleIds || form.roleIds.length === 0) {
      newErrors.roleIds = 'Debe seleccionar al menos un rol.';
    }

    // Sucursales
    if (!form.branchIds || form.branchIds.length === 0) {
      newErrors.branchIds = 'Debe seleccionar al menos una sucursal.';
    }

    // Password opcional: solo se valida si el usuario escribió algo
    if (form.password) {
      if (!evaluatePassword(form.password).isAcceptable) {
        newErrors.password =
          'La contraseña no cumple los requisitos mínimos de seguridad.';
      }
      if ((form.password ?? '') !== (form.confirmPassword ?? '')) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  // ✅ Guardar cambios
  const handleSubmit = () => {
    if (!validateForm()) return;
    onUpdate(form);
  };

  return (
    <FormModal
      isOpen={popupOpen}
      onClose={onClose}
      title="Editar Usuario"
      maxWidth="max-w-4xl"
    >
      <form className="flex flex-col gap-5">

        <UserBasicInfo form={form} onChange={updateField} errors={errors} />

        <section className="rounded-xl border border-stroke bg-white p-5 dark:border-strokedark dark:bg-boxdark-2">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold text-black dark:text-white">
              Accesos
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <UserRoleSelector
              form={form}
              options={dropdownOptions}
              onChange={updateField}
              errors={errors}
            />
            <UserBranchSelector
              form={form}
              options={branchOptions}
              onChange={updateField}
              errors={errors}
            />
          </div>
        </section>

        <section className="rounded-xl border border-stroke bg-white p-5 dark:border-strokedark dark:bg-boxdark-2">
          <div className="mb-4 flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold text-black dark:text-white">
              Estado de la cuenta
            </h3>
          </div>
          <UserStatusToggles form={form} onChange={updateField} />
        </section>

        <UserPasswordSection form={form} onChange={updateField} errors={errors} optional />

        <UserSubmitButton
          isSubmitting={isSubmitting}
          label="Guardar Cambios"
          icon="save"
          onSubmit={handleSubmit}
          onCancel={onClose}
        />

      </form>
    </FormModal>
  );
};

export default UserEditPopup;
